/**
 * A 2D camera over world units: `zoom` is device pixels per world unit, and
 * the view eases toward its targets so zooming and jumping feel smooth.
 */
export class Camera {
  x = 0;
  y = 0;
  zoom = 1;
  /** Viewport in device pixels. */
  width = 1;
  height = 1;
  minZoom = 0.05;
  maxZoom = 400;

  private target = { x: 0, y: 0, zoom: 1 };
  /** World point kept under the cursor while easing a zoom. */
  private anchor: { sx: number; sy: number; wx: number; wy: number } | null = null;

  resize(width: number, height: number): void {
    this.width = width;
    this.height = height;
  }

  toScreen(wx: number, wy: number): [number, number] {
    return [(wx - this.x) * this.zoom + this.width / 2, (wy - this.y) * this.zoom + this.height / 2];
  }

  toWorld(sx: number, sy: number): [number, number] {
    return [(sx - this.width / 2) / this.zoom + this.x, (sy - this.height / 2) / this.zoom + this.y];
  }

  /** Visible world rectangle [x0, y0, x1, y1]. */
  bounds(): [number, number, number, number] {
    const [x0, y0] = this.toWorld(0, 0);
    const [x1, y1] = this.toWorld(this.width, this.height);
    return [x0, y0, x1, y1];
  }

  /** Moves immediately (dragging). */
  panBy(dxScreen: number, dyScreen: number): void {
    this.x -= dxScreen / this.zoom;
    this.y -= dyScreen / this.zoom;
    this.target.x = this.x;
    this.target.y = this.y;
    this.anchor = null;
  }

  /** Zooms by `factor`, keeping the world point under (sx, sy) in place. */
  zoomAt(sx: number, sy: number, factor: number): void {
    const [wx, wy] = this.anchor && this.anchor.sx === sx && this.anchor.sy === sy ? [this.anchor.wx, this.anchor.wy] : this.toWorld(sx, sy);
    this.target.zoom = clamp(this.target.zoom * factor, this.minZoom, this.maxZoom);
    this.anchor = { sx, sy, wx, wy };
  }

  /** Zooms by `factor` immediately around screen point (sx, sy) (pinch). */
  zoomNow(sx: number, sy: number, factor: number): void {
    const [wx, wy] = this.toWorld(sx, sy);
    this.zoom = clamp(this.zoom * factor, this.minZoom, this.maxZoom);
    this.x = wx - (sx - this.width / 2) / this.zoom;
    this.y = wy - (sy - this.height / 2) / this.zoom;
    this.target = { x: this.x, y: this.y, zoom: this.zoom };
    this.anchor = null;
  }

  /** Eases to centre on (x, y), optionally at a zoom. */
  /** Screen covered by the page's bars (device pixels): the visible middle
   *  is what fitting and flying centre on. */
  insets = { top: 0, bottom: 0 };

  /** How far (world units, at `zoom`) the visible middle sits below the canvas centre. */
  private offsetY(zoom: number): number {
    return (this.insets.top - this.insets.bottom) / 2 / zoom;
  }

  flyTo(x: number, y: number, zoom?: number): void {
    const z = zoom === undefined ? this.target.zoom : clamp(zoom, this.minZoom, this.maxZoom);
    this.target = { x, y: y - this.offsetY(z), zoom: z };
    this.anchor = null;
  }

  /** Jumps without easing. */
  set(x: number, y: number, zoom: number): void {
    this.x = this.target.x = x;
    this.y = this.target.y = y;
    this.zoom = this.target.zoom = clamp(zoom, this.minZoom, this.maxZoom);
    this.anchor = null;
  }

  /** Fits a world rectangle in view. */
  fit(x0: number, y0: number, x1: number, y1: number, margin = 0.05, immediate = false): void {
    const visible = Math.max(1, this.height - this.insets.top - this.insets.bottom);
    const zoom = Math.min(this.width / (x1 - x0), visible / (y1 - y0)) * (1 - 2 * margin);
    if (immediate) this.set((x0 + x1) / 2, (y0 + y1) / 2 - this.offsetY(clamp(zoom, this.minZoom, this.maxZoom)), zoom);
    else this.flyTo((x0 + x1) / 2, (y0 + y1) / 2, zoom);
  }

  /** Advances easing; returns whether the view changed. */
  update(dt: number): boolean {
    const k = 1 - Math.exp(-dt * 14);
    const before = [this.x, this.y, this.zoom];
    this.zoom += (this.target.zoom - this.zoom) * k;
    if (Math.abs(this.target.zoom - this.zoom) / this.target.zoom < 1e-4) this.zoom = this.target.zoom;
    if (this.anchor) {
      // Keep the anchored world point under the cursor.
      this.x = this.anchor.wx - (this.anchor.sx - this.width / 2) / this.zoom;
      this.y = this.anchor.wy - (this.anchor.sy - this.height / 2) / this.zoom;
      this.target.x = this.x;
      this.target.y = this.y;
      if (this.zoom === this.target.zoom) this.anchor = null;
    } else {
      this.x += (this.target.x - this.x) * k;
      this.y += (this.target.y - this.y) * k;
    }
    return before[0] !== this.x || before[1] !== this.y || before[2] !== this.zoom;
  }

  /** Follows a moving point (keeps zoom). */
  follow(x: number, y: number): void {
    this.target.x = x;
    this.target.y = y;
    this.anchor = null;
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
